import { LumaRuntimeError }                                 from '../LumaRuntimeError.js';
import { Opcode, Program }                                  from '../Program/index.js';
import { ANSI }                                             from '../Utility/ANSI.js';
import { Deserializer }                                     from './Deserializer.js';
import { DiagnosticInfo, ExecutionResult, StackTraceFrame } from './ExecutionResult.js';
import { InstructionSet }                                   from './InstructionSet.js';
import { Serializer }                                       from './Serializer.js';
import { StackFrame, State }                                from './State.js';
import { HostClassContract, VirtualMachineOptions }         from './VirtualMachineOptions.js';

export type OpcodeProfileEntry = {
    count: number;
    totalTime: number;
    minTime: number;
    maxTime: number;
}

export type OpcodeProfileStats = {
    opcode: string;
    count: number;
    totalTime: number;
    averageTime: number;
    minTime: number;
    maxTime: number;
    percentage: number;
}

export type VirtualMachineProfile = {
    enabled: boolean;
    totalInstructions: number;
    totalTime: number;
    byOpcode: OpcodeProfileStats[];
}

export class VirtualMachine extends InstructionSet
{
    public readonly state: State;

    public budget: number;
    public throwOnError: boolean;

    protected readonly program: Program;
    protected readonly hostFunctions: Map<string, Function>;
    protected readonly hostClasses: Map<string, HostClassContract>;
    protected readonly resolveModule: (moduleName: string) => Program | undefined;
    protected readonly moduleCache: Record<string, any>;
    protected readonly profilingEnabled: boolean;
    protected readonly profileNow: () => number;
    protected readonly recordOpcodeProfile: (opcode: Opcode, duration: number) => void;

    private readonly serializer: Serializer;
    private readonly deserializer: Deserializer;
    private readonly result: ExecutionResult;
    private readonly opcodeProfile: Map<Opcode, OpcodeProfileEntry>;
    private totalProfiledInstructions: number;
    private totalProfiledTime: number;

    constructor(program: Program, options: VirtualMachineOptions = {})
    {
        super();

        ANSI.enabled = options.colors ?? ANSI.autoDetectSupport();

        this.budget        = options.budget ?? Infinity;
        this.program       = program;
        this.hostFunctions = new Map();
        this.hostClasses   = new Map();
        this.serializer    = new Serializer();
        this.deserializer  = new Deserializer();
        this.state         = new State(program, options.variables ?? {});
        this.throwOnError  = options.throwOnError ?? true;
        this.moduleCache   = options.moduleCache ?? {};
        this.profilingEnabled = options.profile ?? false;
        this.profileNow = options.now ?? (() => {
            if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
                return performance.now();
            }

            return Date.now();
        });
        this.resolveModule = options.resolveModule ?? (() => {
            throw new Error('Module resolution is not supported.');
        });
        this.opcodeProfile             = new Map();
        this.totalProfiledInstructions = 0;
        this.totalProfiledTime         = 0;
        this.recordOpcodeProfile       = (opcode: Opcode, duration: number): void => {
            if (! this.profilingEnabled) {
                return;
            }

            const elapsed = Math.max(0, duration);
            let entry     = this.opcodeProfile.get(opcode);
            if (! entry) {
                entry = {
                    count:     0,
                    totalTime: 0,
                    minTime:   Number.POSITIVE_INFINITY,
                    maxTime:   0,
                };

                this.opcodeProfile.set(opcode, entry);
            }

            entry.count++;
            entry.totalTime += elapsed;
            entry.minTime = Math.min(entry.minTime, elapsed);
            entry.maxTime = Math.max(entry.maxTime, elapsed);

            this.totalProfiledInstructions++;
            this.totalProfiledTime += elapsed;
        };

        options.functions = options.functions ?? {};
        if (! ('print' in options.functions)) {
            options.functions.print = (...args: any[]) => {
                console.log(...args);
            };
        }

        for (const [name, fn] of Object.entries(options.functions)) {
            this.registerFunction(name, fn);
        }

        for (const [name, cls] of Object.entries(options.classes ?? {})) {
            this.hostClasses.set(name, cls);
        }

        this.result = {
            isCompleted:     false,
            isSleeping:      false,
            error:           undefined,
            remainingBudget: this.budget,
        };
    }

    /**
     * Register a native function that can be called from the VM.
     *
     * @param {string} name
     * @param {Function} fn
     */
    public registerFunction(name: string, fn: Function): this
    {
        this.hostFunctions.set(name, fn);

        return this;
    }

    /**
     * Register a native class that can be instantiated or extended from the VM.
     *
     * @param {string} name
     * @param {HostClassContract} contract
     */
    public registerClass(name: string, contract: HostClassContract): this
    {
        this.hostClasses.set(name, contract);

        return this;
    }

    /**
     * Returns opcode profiling metrics ordered by total execution time.
     */
    public getProfile(): Readonly<VirtualMachineProfile>
    {
        const byOpcode: OpcodeProfileStats[] = [];

        for (const [opcode, entry] of this.opcodeProfile.entries()) {
            byOpcode.push({
                opcode,
                count:       entry.count,
                totalTime:   entry.totalTime,
                averageTime: entry.count > 0 ? entry.totalTime / entry.count : 0,
                minTime:     Number.isFinite(entry.minTime) ? entry.minTime : 0,
                maxTime:     entry.maxTime,
                percentage:  this.totalProfiledTime > 0 ? (entry.totalTime / this.totalProfiledTime) * 100 : 0,
            });
        }

        byOpcode.sort((a, b) => b.totalTime - a.totalTime);

        return {
            enabled:           this.profilingEnabled,
            totalInstructions: this.totalProfiledInstructions,
            totalTime:         this.totalProfiledTime,
            byOpcode,
        };
    }

    /**
     * Clears all collected profiling samples.
     */
    public resetProfile(): void
    {
        this.opcodeProfile.clear();
        this.totalProfiledInstructions = 0;
        this.totalProfiledTime = 0;
    }

    /**
     * Run the VM for a specific number of "ops".
     *
     * @returns TRUE if the script finished, FALSE if it paused (ran out of budget or crashed).
     */
    public run(deltaTime: number = 16.6): Readonly<ExecutionResult>
    {
        if (this.state.isAwaitingPromise) {
            return this.result;
        }

        // Reset execution result.
        this.result.isCompleted     = false;
        this.result.isSleeping      = false;
        this.result.error           = undefined;
        this.result.remainingBudget = this.budget;

        try {
            if (! this.state.isInsideInterrupt && this.state.eventQueue.length > 0) {
                this.executeNextEvent();
            }

            const [b, c]                = this.execute(this.budget, deltaTime);
            this.result.remainingBudget = b;
            this.result.isCompleted     = c;
            this.result.isSleeping      = this.state.isSleeping;
            this.result.remainingBudget = this.budget;
        } catch (e) {
            if (! (e instanceof Error)) {
                throw e;
            }

            this.result.error       = this.createErrorDiagnosticInfo(e);
            this.result.isCompleted = true;

            if (this.throwOnError) {
                throw new LumaRuntimeError({
                    message:    this.createErrorMessageFromDiagnostic(this.result.error),
                    moduleName: this.state.currentProgram.moduleName,
                    position:   this.state.currentProgram.instructions[this.state.ip]?.pos,
                    program:    this.state.currentProgram,
                    address:    Math.max(0, this.state.ip - 1),
                    cause:      e,
                });
            }
        }

        return this.result;
    }

    /**
     * Returns a list of event names that can be dispatched to the VM.
     *
     * @returns {string[]}
     */
    public get eventNames(): string[]
    {
        return Object.keys(this.program.references.events);
    }

    /**
     * Dispatch an event to the VM.
     *
     * Returns true if a hook for the given event name exists and that the
     * event was successfully queued, false otherwise.
     *
     * @param {string} name - The name of the event.
     * @param {any[]} args  - Arguments to pass to the event handler.
     * @returns {boolean}
     */
    public dispatch(name: string, args: any[] = []): boolean
    {
        const eventInfo = this.program.references.events[name];
        if (! eventInfo) {
            return false;
        }

        this.state.eventQueue.push({name, args});
        this.state.isHalted = false;

        return true;
    }

    /**
     * Saves the current state of the VM to a string.
     *
     * @returns {string}
     */
    public save(): string
    {
        return this.serializer.serialize(this.romHash, this.state);
    }

    /**
     * Restores the VM state from a string.
     *
     * @param {string} state
     */
    public load(state: string): void
    {
        this.deserializer.deserialize(this.romHash, state, this.state);
    }

    /**
     * Returns a ROM hash based on the loaded instructions.
     *
     * @returns {string}
     */
    public get romHash(): string
    {
        let hash = 0;

        for (const instr of this.program.instructions) {
            const opStr = `${instr.op}:${JSON.stringify(instr.arg)}`;
            for (let i = 0; i < opStr.length; i++) {
                const char = opStr.charCodeAt(i);
                hash       = ((hash << 5) - hash) + char;
                hash |= 0; // Convert to 32bit integer
            }
        }

        return hash.toString(16);
    }

    private executeNextEvent(): void
    {
        const event = this.state.eventQueue.shift();
        if (! event) return;

        const eventInfo = this.program.references.events[event.name];

        for (let i = 0; i < eventInfo.numArgs; i++) {
            this.state.push(event.args[i] ?? null);
        }

        this.state.pushFrame(this.state.ip, {
            name:        `<interrupt:${event.name}>`,
            isInterrupt: true,
        });

        this.state.ip = eventInfo.address;
    }

    private createErrorDiagnosticInfo(e: Error): DiagnosticInfo
    {
        return {
            message: e.message,
            source:  this.createErrorSourceLines(this.state.currentProgram, this.state.ip).join('\n'),
            trace:   this.createStackTrace(),
        } satisfies DiagnosticInfo;
    }

    private createStackTrace(): StackTraceFrame[]
    {
        const frames: StackTraceFrame[] = [];
        const stack: StackFrame[]       = [
            ...[...this.state.frames].reverse(),
        ];

        for (const frame of stack) {
            if (frame.ip >= frame.program.instructions.length) continue;

            frames.push({
                type:         frame.isModule ? 'module' : frame.isInterrupt ? 'interrupt' : 'function',
                line:         frame.program.instructions[frame.ip].pos?.lineStart ?? -1,
                column:       frame.program.instructions[frame.ip].pos?.columnStart ?? -1,
                functionName: frame.name,
                moduleName:   frame.moduleName,
                source:       this.createErrorSourceLines(frame.program, frame.ip).join('\n'),
            });
        }

        return frames;
    }

    private createErrorSourceLines(program: Program, ip: number): string[]
    {
        const instr = program.instructions[ip];
        if (! instr?.pos) {
            return [];
        }

        const {lineStart, columnStart} = instr.pos;
        const sourceLines              = program.source.split('\n');

        // Grab the 3 last lines before the error line, the error line itself.
        const result: string[] = [];
        for (let i = Math.max(0, lineStart - 4); i < Math.min(sourceLines.length, lineStart); i++) {
            const prefix = (i + 1 === lineStart) ? '<red>>></red> ' : '   ';
            result.push(`${prefix}<gray>${i + 1}|</gray> ${sourceLines[i]}`.trimEnd());
        }

        // Add a caret pointing to the error column.
        const caretLine = '<gray>' + ('-'.repeat(columnStart + lineStart.toString().length + 4) + '</gray>') + '<red>^</red>';
        result.push(caretLine);

        return result;
    }

    /**
     * Create a human-readable error message from a DiagnosticInfo object.
     *
     * @param {DiagnosticInfo} diag - The diagnostic information.
     * @returns {string} - The formatted error message.
     */
    public createErrorMessageFromDiagnostic(diag: DiagnosticInfo): string
    {
        let msg = `<red>${diag.message}</red>\n`;

        if (diag.source) {
            msg += `\nSource:\n${diag.source}\n`;
        }

        if (diag.trace && diag.trace.length > 0) {
            msg += `\nStack Trace:\n`;
            let id = diag.trace.length;
            for (const frame of diag.trace) {
                id--;
                msg += ` <gray>#${id}: [${frame.type}]</gray> `;
                if (frame.moduleName) {
                    msg += `Module: <cyan>${frame.moduleName}</cyan> `;
                }
                if (frame.functionName && frame.functionName !== '<anonymous>') {
                    msg += `Function: <green>${frame.functionName}</green> `;
                }
                msg += `at line <cyan>${frame.line}</cyan>, column <cyan>${frame.column}</cyan>\n`;
            }
        }

        return ANSI.format(msg);
    }
}
